import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Toast } from 'sharedUi/Toast';
import { Tooltip } from 'sharedUi/Tooltip';
import { Dropdown } from 'sharedUi/Dropdown';
import { Input } from 'sharedUi/Input';
import { Button } from 'sharedUi/Button';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { RefreshButton } from 'sharedUi/RefreshButton';
import { useQuery } from '@tanstack/react-query';
import { unitVillagesService, type UnitVillageQueryParams } from '../../services/unit-villages.service';
import { masterService } from '../../services/master.service';
import { projectsService, type AccessibleProject } from '../../services/projects.service';
import { ProjectCell } from '../../components/common/project-cell';
import { useDebounce } from '../../hooks/useDebounce';
import { extractErrorMessage } from '../../utils/error-handler';
import type { UnitVillage } from '../../types';
import DeleteConfirmDialog from '../../components/dialogs/delete-confirm-dialog';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { useCanCreate, useCanUpdate, useCanDelete } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import PageHeader from '../../components/common/page-header';
import SaasDataTable, {
  type SaasColumn,
  buildDefaultFilters,
  buildColumnFiltersParam,
} from '../../components/common/saas-data-table';
import ShowCoreToggle from '../../components/common/show-core-toggle';
import { formatDate } from 'sharedUi/dateUtils';

// Filterable fields — used to seed the default filter state.
const FILTER_DEFS: SaasColumn[] = [
  { field: 'villageName', header: 'Unit Village Name', filter: true },
  { field: 'mandalName', header: 'Mandal', filter: true },
  { field: 'districtName', header: 'District', filter: true },
];

const UnitVillagesList = ({ embedded = false, appId }: { embedded?: boolean; appId?: string }) => {
  const { navigateToView, navigateToEdit, navigateToCreate } = useSecureNavigation({
    entity: 'unit-villages',
    basePath: '/unit-villages',
  });
  const toast = useRef<Toast>(null);
  const canCreate = useCanCreate(JOB_NAMES.UNIT_VILLAGES) && !appId;
  const canUpdate = useCanUpdate(JOB_NAMES.UNIT_VILLAGES);
  const canDelete = useCanDelete(JOB_NAMES.UNIT_VILLAGES);
  const [unitVillages, setUnitVillages] = useState<UnitVillage[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedMappingId, setSelectedMappingId] = useState<string | null>(null);
  const includeDeleted = false; // "Show deleted" toggle removed; always hide deleted rows
  const [unitIdFilter, setUnitIdFilter] = useState<string>('');
  const [mandalNameFilter, setMandalNameFilter] = useState<string>('');
  // Shown for ONE project (the Data Explorer): the screen opens already
  // narrowed to it, instead of to the session's project.
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(appId ?? null);
  const [showCore, setShowCore] = useState(false);
  const [accessibleProjects, setAccessibleProjects] = useState<AccessibleProject[]>([]);
  useEffect(() => {
    projectsService.getAccessible().then(setAccessibleProjects).catch(() => setAccessibleProjects([]));
  }, []);
  const [searchText, setSearchText] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Pagination states for server-side pagination
  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Sorting states
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<1 | -1 | null>(null);
  const [filters, setFilters] = useState<Record<string, any>>(() => buildDefaultFilters(FILTER_DEFS));

  // Debounce search text with 500ms (changed from 3000ms)
  const debouncedSearchText = useDebounce(searchText, 500);
  const debouncedMandalName = useDebounce(mandalNameFilter, 500);

  const showError = useCallback((message: string) => {
    toast.current?.show({
      severity: 'error',
      summary: 'Error',
      detail: message,
      life: 10000,
    });
  }, []);

  // Fetch dropdown data
  const { data: units = [] } = useQuery({
    queryKey: ['units', 'dropdown'],
    queryFn: masterService.getUnitsForDropdown,
  });

  // Fetch unit villages with filters
  const fetchUnitVillages = useCallback(async (
    page: number = 1,
    size: number = pageSize,
    sortBy: string | null = sortField,
    order: 1 | -1 | null = sortOrder,
    activeFilters: Record<string, any> = filters
  ) => {
    try {
      setLoading(true);

      // Build query parameters
      const params: UnitVillageQueryParams = {
        page,
        page_size: size,
      };

      if (includeDeleted) {
        params.include_deleted = true;
      }

      if (debouncedSearchText && debouncedSearchText.trim()) {
        params.search = debouncedSearchText.trim();
      }

      if (unitIdFilter && unitIdFilter.trim()) {
        params.unitId = unitIdFilter.trim();
      }

      if (debouncedMandalName && debouncedMandalName.trim()) {
        params.mandalName = debouncedMandalName.trim();
      }

      if (selectedProjectId) {
        params.app_id = selectedProjectId;
      }

      params.include_global = showCore;

      // Add sorting parameters
      if (sortBy) {
        params.sort_by = sortBy;
        params.sort_order = order === 1 ? 'asc' : 'desc';
      }

      const filtersParam = buildColumnFiltersParam(activeFilters);
      if (filtersParam) params.filters = filtersParam;

      const response = await unitVillagesService.getAll(params);
      // Map _id to id for DataGrid compatibility
      const mappedData = (response.data || []).map(uv => ({ ...uv, id: uv._id }));
      setUnitVillages(mappedData);

      // Handle both totalItems and total from API response
      const total = response.pagination?.totalItems ?? (response.pagination as any)?.total ?? 0;
      setTotalRecords(total);
    } catch (err: any) {
      console.error('Failed to fetch unit villages:', err);
      showError(extractErrorMessage(err, 'Failed to load unit villages. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, [includeDeleted, debouncedSearchText, unitIdFilter, debouncedMandalName, selectedProjectId, showCore, pageSize, sortField, sortOrder, filters, showError]);

  // Handle page change from DataTable
  const handlePageChange = (event: any) => {
    const newPage = Math.floor(event.first / event.rows) + 1;
    setFirst(event.first);
    setPageSize(event.rows);
    fetchUnitVillages(newPage, event.rows, sortField, sortOrder);
  };

  // Handle sort event - server-side sorting
  const handleSort = (event: any) => {
    const newSortField = event.sortField;
    const newSortOrder = event.sortOrder as 1 | -1 | null;

    setSortField(newSortField);
    setSortOrder(newSortOrder);

    // Reset to first page when sorting changes
    setFirst(0);
    fetchUnitVillages(1, pageSize, newSortField, newSortOrder);
  };

  const handleFilter = (event: any) => {
    const next = event.filters || {};
    setFilters(next);
    setFirst(0);
    fetchUnitVillages(1, pageSize, sortField, sortOrder, next);
  };

  // Reset to first page when filters change
  useEffect(() => {
    setFirst(0);
    fetchUnitVillages(1, pageSize);
  }, [includeDeleted, debouncedSearchText, unitIdFilter, debouncedMandalName, selectedProjectId, showCore]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleView = (id: string | number) => {
    navigateToView(String(id));
  };

  const handleEdit = (id: string | number) => {
    navigateToEdit(String(id));
  };

  const handleDeleteClick = (id: string | number) => {
    setSelectedMappingId(id as string);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedMappingId) return;

    try {
      setDeleteLoading(true);
      await unitVillagesService.delete(selectedMappingId);
      setDeleteDialogOpen(false);
      setSelectedMappingId(null);
      fetchUnitVillages(Math.floor(first / pageSize) + 1, pageSize);
    } catch (err: any) {
      console.error('Failed to delete unit village mapping:', err);
      setDeleteDialogOpen(false);
      setSelectedMappingId(null);
      showError(extractErrorMessage(err, 'Failed to delete mapping'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedMappingId(null);
  };

  // Prepare dropdown options - show names but use IDs
  const unitOptions = [
    { label: 'All Units', value: '' },
    ...units.map(unit => ({
      value: unit._id,
      label: unit.name || (unit as any).policeReferenceId || unit._id,
    })),
  ];

  const columns: SaasColumn<UnitVillage>[] = useMemo(() => {
    const baseColumns: SaasColumn<UnitVillage>[] = [
      {
        field: 'villageName',
        header: 'Unit Village Name',
        sortable: true,
        filter: true,
        hideable: false,
        body: (rowData: UnitVillage) => rowData.villageName || '-',
      },
      {
        field: 'unit',
        header: 'Unit',
        sortable: false,
        body: (rowData: UnitVillage) => rowData.unit?.name || '-',
      },
      {
        field: 'mandalName',
        header: 'Mandal',
        sortable: true,
        filter: true,
        body: (rowData: UnitVillage) => rowData.mandalName || rowData.mandal?.mandalName || '-',
      },
      {
        field: 'districtName',
        header: 'District',
        sortable: true,
        filter: true,
        body: (rowData: UnitVillage) => rowData.districtName || '-',
      },
      {
        field: 'appId',
        header: 'Project',
        body: (rowData: UnitVillage) => <ProjectCell appId={rowData.appId} projects={accessibleProjects} />,
      },
      {
        field: 'createdAt',
        header: 'Created At',
        sortable: true,
        body: (rowData: UnitVillage) =>
          formatDate(rowData.createdAt),
      },
    ];

    // Only show Status column when includeDeleted is active
    if (includeDeleted) {
      baseColumns.push({
        field: 'status',
        header: 'Status',
        sortable: false,
        body: (rowData: UnitVillage) => {
          const deleted = (rowData as any).isDelete || rowData.isDeleted;
          return (
            <span
              className={`saas-status-dot ${deleted ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}
            >
              {deleted ? 'Deleted' : 'Active'}
            </span>
          );
        },
      });
    }

    // Always add actions column at the end
    baseColumns.push({
      field: 'actions',
      header: 'Actions',
      align: 'center',
      hideable: false,
      sortable: false,
      body: (rowData: UnitVillage) => {
        const isDeleted = (rowData as any).isDelete || rowData.isDeleted;

        return (
          <div className="flex gap-1 justify-center">
            <button aria-label="View"
              className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              onClick={() => handleView(rowData._id)}
              data-testid="UnitVillagesList.Action.View"
              data-pr-tooltip="View"
            >
              <i className="pi pi-eye text-sm text-green-600 dark:text-green-400" style={{ fontSize: '1.125rem' }} />
            </button>
            <Tooltip target="[data-pr-tooltip]" />

            {!isDeleted && (
              <>
                {canUpdate && (
                  <button aria-label="Edit"
                    className="p-2 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                    onClick={() => handleEdit(rowData._id)}
                    data-testid="UnitVillagesList.Action.Edit"
                    data-pr-tooltip="Edit"
                  >
                    <i className="pi pi-pencil text-blue-600" style={{ fontSize: '1.125rem' }} />
                  </button>
                )}

                {canDelete && (
                  <button aria-label="Delete"
                    className="p-2 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    onClick={() => handleDeleteClick(rowData._id)}
                    data-testid="UnitVillagesList.Action.Delete"
                    data-pr-tooltip="Delete"
                  >
                    <i className="pi pi-trash text-red-600" style={{ fontSize: '1.125rem' }} />
                  </button>
                )}
              </>
            )}

          </div>
        );
      },
    });

    return baseColumns;
  }, [canUpdate, canDelete, includeDeleted, accessibleProjects]);

  return (
    <PermissionGuard jobName={JOB_NAMES.UNIT_VILLAGES}>
      <Toast ref={toast} position="top-right" />
      <div className={embedded ? '' : 'p-3'} data-testid="SCR-UnitVillages-List">
        <PageHeader
          embedded={embedded}
          icon="pi pi-home"
          title="Unit Villages Management"
          subtitle="Manage village-to-unit mappings and jurisdictions"
          testId="UnitVillages.Header"
        />

        <div className={embedded ? '' : 'bg-surface rounded-xl shadow-sm border border-line overflow-hidden'}>
          <SaasDataTable
            tableId="unit-villages"
            columns={appId ? columns.filter((c) => c.field !== 'appId') : columns}
            data={unitVillages}
            loading={loading}
            dataKey="_id"
            emptyMessage="No unit villages found"
            first={first}
            rows={pageSize}
            totalRecords={totalRecords}
            onPage={handlePageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sortField={sortField || undefined}
            sortOrder={sortOrder || undefined}
            onSort={handleSort}
            filters={filters}
            onFilter={handleFilter}
            globalSearch={searchText}
            onGlobalSearchChange={setSearchText}
            globalSearchPlaceholder="Search by village name..."
            testId="UnitVillagesList"
            toolbarStart={
              <div className="flex items-center gap-2 flex-wrap">
                {!appId && accessibleProjects.length > 1 && (
                  <Dropdown
                    value={selectedProjectId}
                    options={accessibleProjects.map((p) => ({
                      label: p.appCode ? `${p.name} (${p.appCode})` : p.name,
                      value: p._id,
                    }))}
                    onChange={(e: any) => setSelectedProjectId(e.value || null)}
                    placeholder="Project"
                    showClear
                    style={{ minWidth: '160px', width: '160px' }}
                    data-testid="UnitVillagesList.Filter.Project"
                    resetFilterOnHide={true}
                  />
                )}
                <Dropdown
                  value={unitIdFilter}
                  options={unitOptions}
                  onChange={(e) => setUnitIdFilter(String(e.value || ''))}
                  placeholder="Unit"
                  style={{ width: '150px' }}
                  data-testid="UnitVillagesList.Filter.Unit"
                  resetFilterOnHide={true}
                />

                <Input
                  name="mandalName"
                  placeholder="Mandal"
                  value={mandalNameFilter}
                  onChange={(value: string) => setMandalNameFilter(value)}
                  testId="UnitVillagesList.Filter.Mandal"
                  style={{ minWidth: '150px', width: '150px' }}
                  clearable
                />

                <ShowCoreToggle checked={showCore} onChange={setShowCore} testId="UnitVillage.Toggle.ShowCore" />
              </div>
            }
            toolbarEnd={
              <>
                <span className="text-sm text-ink-muted whitespace-nowrap">
                  {totalRecords} {totalRecords === 1 ? 'record' : 'records'}
                </span>
                <RefreshButton
                  onClick={() => fetchUnitVillages(Math.floor(first / pageSize) + 1, pageSize, sortField, sortOrder)}
                  loading={loading}
                  testId="UnitVillagesList.Button.Refresh"
                />
                {canCreate && (
                  <Button
                    label="Create Mapping"
                    icon="pi pi-plus"
                    onClick={() => navigateToCreate()}
                    testId="UnitVillagesList.Button.Create"
                    size="small"
                  />
                )}
              </>
            }
          />
        </div>

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmDialog
          visible={deleteDialogOpen}
          onCancel={handleDeleteCancel}
          onDelete={handleDeleteConfirm}
          message="Are you sure you want to delete this unit-village mapping? This action will mark the mapping as deleted."
          testId="UnitVillagesList.Dialog.Delete"
          loading={deleteLoading}
        />

      </div>
    </PermissionGuard>
  );
};

export default UnitVillagesList;
